import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { CheckIndividualEmailResponse, useToast } from '@aslanpay/portal-common-lib';

import useCheckIndividualEmail from 'app/hooks/useCheckIndividualEmail';

import { useAuthenticationStore } from 'app/store';

import { authenticationSchema } from 'app/utils/schemas';

import { AuthenticationStepProps, CheckEmailFormFields } from '../types';

function CheckEmail({ onSetStep }: AuthenticationStepProps) {
  const toast = useToast();

  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CheckEmailFormFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.checkEmail),
    defaultValues: { email: authenticationData.email },
  });

  const { mutate: checkEmail, isLoading: isCheckingEmail } = useCheckIndividualEmail({
    onSuccess: (data: CheckIndividualEmailResponse) => {
      switch (data.result) {
        case 1:
          onSetStep('login');
          break;
        case 2:
          setAuthenticationData({ ...authenticationData, transactionId: data.transactionId });
          onSetStep('verifyEmail');
          break;
        case 3:
          onSetStep('invalidEmail');
          break;
        default:
          onSetStep('invalidEmail');
      }
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to check your email. Please try again.',
      });
    },
  });

  const handleCheckEmail = (data: CheckEmailFormFields) => {
    setAuthenticationData({ ...authenticationData, email: data.email });
    checkEmail({ email: data.email });
  };

  return (
    <Flex flexDir="column" mt="35px">
      <Text as="h2" textStyle="h2" color="brand.darkPurple">
        Welcome to Aslan
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        To sign up or log in, please enter your work email below.
      </Text>
      <form onSubmit={handleSubmit(handleCheckEmail)} noValidate>
        <FormControl isInvalid={!!errors?.email?.message} isRequired>
          <FormLabel>Email</FormLabel>
          <Input type="email" placeholder="Enter your email" {...register('email')} />
          <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
        </FormControl>
        <Text mt="42px" mb="22px" color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>
        <Button w="full" size="lg" type="submit" isLoading={isCheckingEmail} isDisabled={!isValid}>
          Next
        </Button>
      </form>
    </Flex>
  );
}

export default CheckEmail;
